import React from 'react';
import ChatSourcesPanel from '../components/ChatSourcesPanel/ChatSourcesPanel';
import MarkdownContent from '../components/MarkdownContent/MarkdownContent';
import MessageGraphs from '../components/MessageGraphs/MessageGraphs';
import TalentPulseStepDropdown from '../components/TalentPulseStepDropdown/TalentPulseStepDropdown';
import { SSE_EVENTS } from '../constant/keyConstants';

/** Matches any {{CHART_<hex-or-word-chars>}} placeholder. */
const CHART_PLACEHOLDER_RE = /\{\{(CHART_[A-Za-z0-9_]+)\}\}/g;

export function hasChartPlaceholder(text) {
    if (!text || typeof text !== 'string') return false;
    CHART_PLACEHOLDER_RE.lastIndex = 0;
    return CHART_PLACEHOLDER_RE.test(text);
}

export function parseChartPlaceholders(text) {
    if (!text || typeof text !== 'string') return [{ type: 'text', value: text || '' }];

    const segments = [];
    let lastIndex = 0;
    CHART_PLACEHOLDER_RE.lastIndex = 0;
    let match;

    while ((match = CHART_PLACEHOLDER_RE.exec(text)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
        }
        segments.push({ type: 'chart', key: match[1] });
        lastIndex = CHART_PLACEHOLDER_RE.lastIndex;
    }

    if (lastIndex < text.length) {
        segments.push({ type: 'text', value: text.slice(lastIndex) });
    }

    if (segments.length === 0) {
        segments.push({ type: 'text', value: text });
    }

    return segments;
}

export const InlineChartContent = ({
    content = '',
    charts = {},
    graphVariant = 'default',
    isStreaming = false,
    useMathJax = false,
}) => {
    const segments = parseChartPlaceholders(content);

    return (
        <div className="inline-chart-content">
            {segments.map((seg, i) => {
                if (seg.type === 'chart') {
                    const graphData = charts?.[seg.key];
                    if (!graphData) return null;
                    return (
                        <MessageGraphs
                            key={`chart-${seg.key}-${i}`}
                            graph={graphData}
                            variant={graphVariant}
                        />
                    );
                }

                if (!seg.value) return null;

                return (
                    <MarkdownContent
                        key={`text-${i}`}
                        content={seg.value}
                        isStreaming={isStreaming && i === segments.length - 1}
                        useMathJax={useMathJax}
                        enableSectionCopy={true}
                    />
                );
            })}
        </div>
    );
};

/** Tools whose output should render as downloadable transcripts */
export const TP_TRANSCRIPT_TOOLS = new Set([
    'rag',
    'search_interviews',
    'talent_pulse_transcript',
    'transcript',
    'transcripts',
    'search_transcript',
    'interview_transcript'
]);
export const TP_TOOL_DIVIDER = '<hr class="tool-divider" />';

/** Convert a tool key into a clean display label */
export const formatToolLabel = (toolName) => {
    if (!toolName || toolName.toLowerCase() === "answer" || toolName.toLowerCase() === "assistant") return "";
    const lower = toolName.toLowerCase();
    if (TP_TRANSCRIPT_TOOLS.has(lower)) return "Transcripts Data";
    if (lower === "upl_genie" || lower === "genie" || lower === "query_hr_data") return "Internal Data";
    if (lower === "render_chart" || lower === "chart" || lower === "render_graph" || lower === "graph") return "Chart";
    if (lower === "web_search") return "Web Search";
    return toolName
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Backwards-compatible content formatter */
export const formatTalentPulseContent = (raw, sessionId) => {
    if (!raw) return { content: "", citations: [] };
    let data = raw;
    if (typeof data === "string" && data.trim().startsWith("{")) {
        try { data = JSON.parse(data.trim()); } catch (e) { }
    }
    if (typeof data === "string") return { content: data, citations: [] };
    if (typeof data === "object" && data !== null) {
        const parts = [];
        const citations = [];
        Object.entries(data).forEach(([tool, val]) => {
            const text = (typeof val === "object" ? val?.content || "" : String(val || "")).trim();
            if (!text) return;
            const sources = (typeof val === "object" && (val?.sources || val?.citations)) || [];
            if (sources.length) citations.push({ toolName: tool, sources, sessionId });
            const label = formatToolLabel(tool);
            parts.push(label ? `### ${label}\n${text}` : text);
        });
        return { content: parts.join(`\n\n${TP_TOOL_DIVIDER}\n\n`), citations };
    }
    return { content: "", citations: [] };
};

export const getTranscriptCitations = (citations = []) =>
    Array.isArray(citations)
        ? citations.filter((g) => g?.toolName && TP_TRANSCRIPT_TOOLS.has(g.toolName.toLowerCase()))
        : [];

export const getNonTranscriptCitations = (citations = []) =>
    Array.isArray(citations)
        ? citations.filter((g) => !g?.toolName || !TP_TRANSCRIPT_TOOLS.has(g.toolName.toLowerCase()))
        : [];

/**
 * Normalizes and sorts a candidate's career timeline array.
 * Ensures the Current Organisation is placed at index 0 and labeled "Current org",
 * while previous organisations follow in reverse-chronological order (most recent first).
 *
 * @param {Array} rawTimeline - Career history array from profile API or candidate object.
 * @param {string} currentCompany - Candidate's current company name or currentOrg string for matching.
 * @returns {Array} Normalized timeline array with correct labels and ordering.
 */
export function getNormalizedCareerTimeline(rawTimeline, currentCompany = '') {
    if (!Array.isArray(rawTimeline) || rawTimeline.length === 0) {
        return [];
    }

    const parseYears = (str = '') => {
        const text = String(str);
        const matches = text.match(/\b(19|20)\d{2}\b/g);
        const hasCurrentKeywords = /present|current|till date|ongoing|now/i.test(text);

        let startYear = 0;
        let endYear = 0;

        if (matches && matches.length > 0) {
            const years = matches.map(Number);
            startYear = Math.min(...years);
            endYear = Math.max(...years);
        }

        if (hasCurrentKeywords) {
            endYear = 9999;
        }

        return { startYear, endYear, hasCurrentKeywords };
    };

    const cleanCompanyStr = (str = '') => String(str).toLowerCase().trim();
    const targetComp = cleanCompanyStr(currentCompany).split('·')[0].split('—')[0].trim();

    // Map items with metadata for sorting
    const items = rawTimeline.map((item, originalIndex) => {
        const comp = item.company || item.organization || item.org || '';
        const r = item.role || item.designation || '';
        const ten = item.tenure || item.duration || '';
        const cleanComp = cleanCompanyStr(comp);

        const { startYear, endYear, hasCurrentKeywords } = parseYears(ten);

        const isExplicitCurrent = Boolean(
            item.is_current || item.isCurrent || item.current || item.status === 'current'
        );

        const isCompanyMatch = Boolean(
            targetComp && cleanComp && (
                cleanComp === targetComp || targetComp.includes(cleanComp) || cleanComp.includes(targetComp)
            )
        );

        return {
            ...item,
            originalIndex,
            comp,
            role: r,
            tenure: ten,
            startYear,
            endYear,
            isCurrentCandidate: isExplicitCurrent || isCompanyMatch || hasCurrentKeywords
        };
    });

    // Determine current org item
    let currentItem = items.find(it => it.isCurrentCandidate);

    if (!currentItem) {
        const maxEndYear = Math.max(...items.map(it => it.endYear));
        if (maxEndYear > 0) {
            const topByDate = items.filter(it => it.endYear === maxEndYear);
            topByDate.sort((a, b) => b.startYear - a.startYear);
            currentItem = topByDate[0];
        } else {
            currentItem = items[items.length - 1];
        }
    }

    // Sort remaining previous organizations descending by endYear/startYear
    const prevItems = items.filter(it => it !== currentItem);
    prevItems.sort((a, b) => {
        if (b.endYear !== a.endYear) return b.endYear - a.endYear;
        if (b.startYear !== a.startYear) return b.startYear - a.startYear;
        return b.originalIndex - a.originalIndex;
    });

    const orderedItems = [currentItem, ...prevItems];

    // Assign appropriate labels
    return orderedItems.map((item, index) => {
        const isCurrent = index === 0;
        const defaultPrevLabel = orderedItems.length > 2 ? `Prev organisation ${index}` : 'Prev organisation';
        const label = isCurrent ? 'Current org' : (item.label && item.label !== 'Current org' ? item.label : defaultPrevLabel);

        return {
            ...item,
            label,
            isCurrent
        };
    });
}

/** Group steps into dropdown blocks for rendering */
export const groupTalentPulseSteps = (steps) => {
    if (!Array.isArray(steps) || !steps.length) return { blocks: [], finalResponseContent: null };

    const blocks = [];
    let currentThinkingBlock = null;
    let finalResponseContent = null;

    steps.forEach((step) => {
        if (!step || typeof step !== "object" || !step.type) return;

        if (step.type === "final_response") {
            finalResponseContent = step.content || finalResponseContent;
            return;
        }

        if (step.type === "thinking") {
            currentThinkingBlock = {
                id: `block-thinking-${blocks.length}`,
                type: "thinking_group",
                items: [step],
            };
            blocks.push(currentThinkingBlock);
        } else if (step.type === "tool_result") {
            if (currentThinkingBlock) {
                currentThinkingBlock.items.push(step);
            } else {
                blocks.push({
                    id: `block-tool-${blocks.length}`,
                    type: "tool_result_group",
                    items: [step],
                });
            }
        }
    });

    const validBlocks = blocks.filter((b) =>
        b.items.some((it) => {
            if (it.status === "processing") return true;
            const CHART_RE = /\{\{CHART_[A-Za-z0-9_]+\}\}/g;
            const hasText = typeof it.content === "string" && it.content.replace(CHART_RE, "").trim().length > 0;
            const hasCitations = Array.isArray(it.citations) && it.citations.length > 0;
            const hasGraph = !!(it.graph || it.graph_json || it.charts);
            return hasText || hasCitations || hasGraph;
        })
    );

    return { blocks: validBlocks, finalResponseContent };
};

/** Extract main response string from object/content */
export const getAssistantMainContent = (rawContent) => {
    if (!rawContent) return "";
    let obj = rawContent;
    if (typeof obj === "string" && obj.trim().startsWith("{")) {
        try { obj = JSON.parse(obj.trim()); } catch (e) { }
    }
    if (typeof obj === "object" && obj !== null) {
        const target = obj.final_response || obj.assistant || obj.genie;
        if (target) {
            return typeof target === "object" ? target.content || target.text || "" : String(target);
        }
    }
    return typeof rawContent === "string" ? rawContent : "";
};

/** Process stream SSE events into steps array */
export const handleTalentPulseStreamEvent = (event, currentSteps = [], currentCitations = [], sessionId = "") => {
    const eventType = event?.type;
    const eventData = event?.data || {};

    const steps = currentSteps.map((s) => ({ ...s }));
    let citations = [...currentCitations];
    let isComplete = false;
    let finalResponseData = null;

    const rawTool = eventData.tool || "assistant";
    const formattedTool = formatToolLabel(rawTool);
    const rawLabel = (eventData.label && eventData.label !== rawTool) ? eventData.label : (formattedTool || eventData.message || (rawTool === "assistant" ? "Thinking" : rawTool));
    const isAssistantOrFinal = rawTool === "assistant" || rawTool === "final_response";

    const existingFinalStep = steps.find((s) => s.type === "final_response");

    if (eventType === SSE_EVENTS.PROGRESS || eventType === SSE_EVENTS.STEP || eventType === "step" || eventType === SSE_EVENTS.TOOL_START || eventType === "tool_start") {
        // Only classify as final_response when the label/tool explicitly says so
        // Never classify "Thinking" events as final_response even if hasTools is true
        const isFinal = rawTool !== "assistant"
            ? false
            : (rawLabel === "Final Response" || rawTool === "final_response");
        const type = !isAssistantOrFinal ? "tool_result" : (isFinal ? "final_response" : "thinking");

        if (isFinal) {
            steps.forEach((s) => { s.status = "done"; });
        } else if (rawTool !== "assistant") {
            steps.forEach((s) => {
                if (s.type === "thinking" && s.status === "processing") {
                    s.status = "done";
                }
            });
        }

        let activeStep;
        if (type === "thinking") {
            // Never reactivate an already completed thinking step; create a new step if current thinking step is done
            activeStep = steps.find((s) => s.type === "thinking" && s.status === "processing");
        } else {
            // Only reactivate tool step if it is currently processing (not done)
            activeStep = steps.find((s) => s.tool === rawTool && s.type === type && s.status === "processing");
        }

        if (activeStep) {
            activeStep.label = rawLabel;
            activeStep.status = "processing";
        } else {
            const newStep = {
                id: `step-${steps.length}`,
                tool: rawTool,
                label: isFinal ? "Final Response" : rawLabel,
                type,
                status: "processing",
                content: "",
                citations: [],
            };
            steps.push(newStep);
        }

    } else if (eventType === SSE_EVENTS.TOKEN || eventType === "token") {
        const tokenText =
            eventData.token ||
            (typeof eventData.delta === "string" ? eventData.delta : eventData.delta?.content) ||
            eventData.text || eventData.content || "";

        if (tokenText) {
            const isTableToken = typeof tokenText === "string" && (tokenText.trim().startsWith("|") || tokenText.includes("| ---") || tokenText.includes("\n|"));
            if (isTableToken) {
                steps.forEach((s) => {
                    if (s.type === "thinking" && s.status === "processing") {
                        s.status = "done";
                    }
                });
            }

            // Route to final_response when:
            // 1. Explicitly labelled "Final Response" or tool is "final_response"
            // 2. Contains Markdown table syntax
            // 3. final_response step already exists AND no thinking step is currently running
            const hasActiveThinking = steps.some((s) => s.type === "thinking" && s.status === "processing");
            const isFinalToken = isAssistantOrFinal && (
                rawLabel === "Final Response" ||
                rawTool === "final_response" ||
                (!!existingFinalStep && !hasActiveThinking)
            );
            const targetType = !isAssistantOrFinal ? "tool_result" : (isFinalToken ? "final_response" : "thinking");

            let activeStep;
            if (isAssistantOrFinal) {
                if (targetType === "thinking") {
                    activeStep = steps.filter((s) => s.type === "thinking" && s.status === "processing").pop() ||
                        steps.filter((s) => s.type === "thinking").pop();
                } else {
                    activeStep = steps.find((s) => s.type === "final_response");
                }
            } else {
                activeStep = steps.find((s) => s.tool === rawTool && s.type === targetType && s.status === "processing") ||
                    steps.find((s) => s.tool === rawTool && s.status === "processing");
            }

            if (!activeStep) {
                activeStep = {
                    id: `step-${steps.length}`,
                    tool: rawTool,
                    label: isFinalToken ? "Final Response" : rawLabel,
                    type: targetType,
                    status: "processing",
                    content: "",
                    citations: [],
                };
                steps.push(activeStep);
            } else {
                activeStep.status = "processing";
            }
            activeStep.content += tokenText;
        }

    } else if (eventType === SSE_EVENTS.CITATIONS || eventType === SSE_EVENTS.SOURCES || eventType === "citations") {
        const rawSources = eventData.sources || eventData.citations || eventData.web_citations || [];
        if (rawSources.length) {
            const sourcesWithSession = rawSources.map((s) => ({
                ...s,
                session_id: s.session_id || s.sessionId || sessionId || "",
                sessionId: s.sessionId || s.session_id || sessionId || "",
            }));
            let activeStep =
                steps.findLast((s) => s.tool === rawTool && s.status === "processing") ||
                steps.findLast((s) => s.tool === rawTool) ||
                steps[steps.length - 1];
            if (activeStep) activeStep.citations = [...(activeStep.citations || []), ...sourcesWithSession];
            citations.push({ toolName: rawTool, sources: sourcesWithSession, sessionId });
        }

    } else if (eventType === SSE_EVENTS.TOOL_END || eventType === "tool_end") {
        steps.forEach((s) => {
            if (s.tool === rawTool || (rawTool === "assistant" && s.type === "thinking")) {
                s.status = "done";
            }
        });

    } else if (eventType === SSE_EVENTS.CHART || eventType === "chart") {
        const chartTool = eventData.tool || "render_chart";
        const chartLabel = formatToolLabel(chartTool) || eventData.title || eventData.label || "Chart";
        const graphData = eventData.graph_json || eventData.graph || eventData;
        let chartStep = steps.find((s) => (s.tool === chartTool || s.tool === "chart") && s.type === "tool_result");
        if (!chartStep) {
            chartStep = {
                id: `step-${steps.length}`,
                tool: chartTool,
                label: chartLabel,
                type: "tool_result",
                status: "done",
                content: "",
                graph: graphData,
                graph_json: eventData.graph_json,
                citations: [],
            };
            steps.push(chartStep);
        } else {
            chartStep.status = "done";
            chartStep.label = chartLabel;
            if (graphData) {
                chartStep.graph = graphData;
                if (eventData.graph_json) chartStep.graph_json = eventData.graph_json;
            }
        }
    } else if (eventType === SSE_EVENTS.FINAL || eventType === SSE_EVENTS.DONE || eventType === "done" || eventType === SSE_EVENTS.STREAM_COMPLETE) {
        steps.forEach((s) => { s.status = "done"; });
        if (eventData) finalResponseData = eventData;
        isComplete = true;
    }

    return { steps, citations, isComplete, finalResponseData };
};

/** Strip tables for voice TTS */
export const stripTablesForSpeech = (text) => {
    if (!text || typeof text !== "string") return "";
    let cleaned = text.replace(/<table[\s\S]*?<\/table>/gi, "");
    cleaned = cleaned.replace(/(?:(?:^[ \t]*\|.*\|[ \t]*$)+(?:\r?\n|$))/gm, (block) => {
        if (/\|?\s*[-:]{2,}\s*\|/m.test(block) || /^[ \t]*\|.*\|[ \t]*$/m.test(block)) return "\n";
        return block;
    });
    cleaned = cleaned.replace(/^[ \t]*\|.*\|[ \t]*$/gm, "");
    cleaned = cleaned.replace(/\n{3,}/g, "\n\n");
    return cleaned.trim();
};

/** Extract strictly final_response text for voice */
export const extractFinalResponseFromTalentPulse = (content, steps = null, rawContent = null) => {
    let resultText = "";

    if (Array.isArray(steps) && steps.length > 0) {
        const { blocks, finalResponseContent } = groupTalentPulseSteps(steps);
        if (finalResponseContent) {
            resultText = typeof finalResponseContent === "object"
                ? finalResponseContent.content || finalResponseContent.text || ""
                : String(finalResponseContent);
        } else if (blocks.length > 0) {
            return "";
        }
    }

    if (!resultText) {
        const sourceObj = rawContent || content;
        if (typeof sourceObj === "object" && sourceObj !== null) {
            const mainText = getAssistantMainContent(sourceObj);
            if (mainText) resultText = mainText;
        }
    }

    if (!resultText && typeof content === "string") {
        if (content.includes(TP_TOOL_DIVIDER)) {
            const parts = content.split(TP_TOOL_DIVIDER);
            const lastPart = parts[parts.length - 1].trim();
            resultText = lastPart.replace(/^###\s+[^\n]+\n/i, "").trim();
        } else {
            resultText = content;
        }
    }

    return stripTablesForSpeech(resultText || (typeof content === "string" ? content : ""));
};

/** Render downloadable citation cards */
export const renderCitationPanels = (citations, handleCitationDownload) => {
    if (!Array.isArray(citations) || !citations.length) return null;

    return citations.map((citGroup, citIdx) => {
        const isTranscript = !!citGroup.toolName && TP_TRANSCRIPT_TOOLS.has(citGroup.toolName.toLowerCase());
        const sources = (citGroup.sources || [])
            .map((c) => ({
                label: c.title || c.filename || c.file_name || c.text || '',
                href: c.url || c.uri || '#',
                fileId: c.file_id || '',
                fileName: c.file_name || c.filename || c.title || '',
                sessionId: c.session_id || c.sessionId || citGroup.sessionId || citGroup.session_id || '',
                date: c.interview_date || c.date || c.refresh_date || c.retrieved_date || c.last_modified || c.updated_at || c.timestamp || '',
            }))
            .filter((s) => s.label);

        if (!sources.length) return null;

        return (
            <ChatSourcesPanel
                key={citIdx}
                type={isTranscript ? 'transcript' : 'web'}
                sources={sources}
                onItemClick={
                    isTranscript
                        ? (item) => handleCitationDownload(item.sessionId, item.fileId, item.fileName)
                        : undefined
                }
            />
        );
    });
};

/** Main TalentPulse message renderer */
export const renderTalentPulseContent = ({
    content = "",
    citations = [],
    steps = null,
    rawContent = null,
    isStreaming = false,
    handleCitationDownload,
    /**
     * Map of chart_key → Plotly graph object.
     * When provided and `content` contains {{CHART_xxx}} placeholders,
     * uses InlineChartContent to substitute them inline.
     */
    charts = null,
    graphVariant = "compact",
}) => {
    const transcriptCitations = getTranscriptCitations(citations);
    const hasTranscriptCitations = transcriptCitations.length > 0 && !isStreaming;
    const { blocks, finalResponseContent } = groupTalentPulseSteps(steps);
    const displayContent = finalResponseContent || (rawContent ? getAssistantMainContent(rawContent) : "") || content;

    if (isStreaming && blocks.length === 0 && !displayContent) {
        return (
            <div className="loader-inner-section">
                <div className="loading-pulse"></div>
                <span className="loading-text">Loading...</span>
            </div>
        );
    }

    /**
     * Resolve which content renderer to use for a piece of text.
     * If `charts` is non-empty and `text` contains {{CHART_xxx}} tokens,
     * render via InlineChartContent (inline substitution).
     * Otherwise fall back to plain MarkdownContent.
     */
    const renderTextContent = (text, streaming = false) => {
        if (charts && Object.keys(charts).length > 0 && hasChartPlaceholder(text)) {
            return (
                <InlineChartContent
                    content={text}
                    charts={charts}
                    graphVariant={graphVariant}
                    isStreaming={streaming}
                    useMathJax={false}
                />
            );
        }
        return (
            <MarkdownContent
                content={text}
                isStreaming={streaming}
                useMathJax={false}
                enableSectionCopy={true}
            />
        );
    };

    if (blocks.length > 0) {
        return (
            <div className="tp-content-wrapper">
                {blocks.map((block) => (
                    <TalentPulseStepDropdown
                        key={block.id}
                        block={block}
                        defaultExpanded={isStreaming || block.items.some((it) => it.status === "processing")}
                        handleCitationDownload={handleCitationDownload}
                        isStreaming={isStreaming}
                        charts={charts}
                        graphVariant={graphVariant}
                    />
                ))}
                {displayContent && renderTextContent(displayContent, isStreaming)}
            </div>
        );
    }

    return (
        <>
            {renderTextContent(displayContent, isStreaming)}
            {hasTranscriptCitations && renderCitationPanels(transcriptCitations, handleCitationDownload)}
        </>
    );
};

// ==========================================
// Survey Utility Functions & Constants
// ==========================================

export const QUESTION_TYPE_OPTIONS = [
    { id: "scale_5", name: "Scale 1–5" },
    { id: "single_choice", name: "Single choice" },
    { id: "short_text", name: "Short text" },
];

export const normalizeQuestionType = (type) => {
    if (!type) return "scale_5";
    if (type === "rating" || type === "scale" || type === "scale_5") return "scale_5";
    if (type === "choice" || type === "single_choice") return "single_choice";
    if (type === "text" || type === "short_text") return "short_text";
    return type;
};

export const generateSurveyQuestionId = (existingId) => {
    if (typeof existingId === "string" && existingId.startsWith("q_") && existingId.length >= 10) {
        return existingId;
    }
    const hex = Array.from({ length: 18 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
    return `q_${hex}`;
};

export const mapQuestionsToPayload = (questions = []) => {
    if (!Array.isArray(questions)) return [];
    return questions.map((question, index) => {
        const mappedType = normalizeQuestionType(question.type);
        const id = generateSurveyQuestionId(question.id);
        const order = typeof question.order === "number" ? question.order : index + 1;
        const text = (question.text || "").trim();

        if (mappedType === "scale_5") {
            return {
                id,
                order,
                text,
                type: "scale_5",
                required: question.required !== undefined ? Boolean(question.required) : true,
                scaleStyle: question.scaleStyle || "numeric",
                anchors: {
                    low: question.anchors?.low || "1 = not at all",
                    high: question.anchors?.high || "5 = completely",
                },
            };
        }

        if (mappedType === "single_choice") {
            const options = Array.isArray(question.options)
                ? question.options
                    .map((opt) => (typeof opt === "string" ? opt.trim() : String(opt?.label || opt?.text || opt).trim()))
                    .filter(Boolean)
                : Array.isArray(question.choices)
                    ? question.choices
                        .map((c) => (typeof c === "string" ? c.trim() : String(c).trim()))
                        .filter(Boolean)
                    : (question.options || "")
                        .split(",")
                        .map((opt) => opt.trim())
                        .filter(Boolean);

            return {
                id,
                order,
                text,
                type: "single_choice",
                required: question.required !== undefined ? Boolean(question.required) : true,
                options,
            };
        }

        if (mappedType === "short_text") {
            return {
                id,
                order,
                text,
                type: "short_text",
                required: question.required !== undefined ? Boolean(question.required) : false,
                maxLength: question.maxLength ? Number(question.maxLength) : 120,
            };
        }

        return {
            id,
            order,
            text,
            type: mappedType,
            required: question.required !== undefined ? Boolean(question.required) : true,
            ...(question.maxLength ? { maxLength: Number(question.maxLength) } : {}),
            ...(question.options ? { options: question.options } : {}),
        };
    });
};

export const mapSurveyToCard = (survey) => {
    const questions = Array.isArray(survey.questions) ? survey.questions : [];
    const title = survey.survey_name || survey.name || survey.title || "Untitled survey";
    const description = survey.survey_description || survey.description || "";
    const estimate = Math.max(1, Math.round(questions.length * 0.5));
    const surveySendId = survey.survey_send_id || survey.send_id;
    return {
        ...survey,
        id: surveySendId || survey.id || survey.survey_id,
        survey_send_id: surveySendId,
        send_id: surveySendId,
        title,
        survey_name: survey.survey_name || title,
        survey_description: description,
        tag: survey.status || "Published",
        tagTheme: "purple",
        iconType: "lightning",
        description,
        questions,
        questionsCount: survey.questions_count ?? questions.length,
        estimatedTime: `~${estimate} min to answer`,
        lastSent: survey.updated_at || survey.created_at
            ? new Date(survey.updated_at || survey.created_at).toLocaleDateString()
            : "Published",
    };
};

export const getSurveyRows = (response) => {
    if (Array.isArray(response)) return response;
    const rows = response?.sends || response?.surveys || response?.items || response?.results || response?.data || [];
    return Array.isArray(rows) ? rows : getSurveyRows(rows);
};

export const getUserRows = (response) => {
    if (Array.isArray(response)) return response;
    const rows = response?.users || response?.items || response?.results || response?.data || [];
    return Array.isArray(rows) ? rows : getUserRows(rows);
};

export const normalizeActionResult = (result) => result?.error
    ? { data: result.data, error: result.error }
    : { data: result, error: null };